<template>
  <div>
    <!--Remove Deposit Confirmation Modal -->
    <hl-Modal
      v-if="showRemoveDepositModal"
      iconClass="budicon-trash text-danger"
      :title="`Er du sikker på at du vil slette data om ${getTenancyDepositCategory}?`"
      class="text-center"
    >
      <div>
        <p>Alle data vil gå tapt ved sletting.</p>

        <div class="mt-5">
          <button class="btn btn-danger mx-2" @click="deleteDeposit">Bekreft</button>
          <button class="btn btn-outline-secondary mx-2" @click="showRemoveDepositModal = false">Avbryt</button>
        </div>
      </div>
    </hl-Modal>
    <!-- /Remove Deposit Confirmation Modal -->
    <BlockContainer class="p-5 deposit">
      <ValidationObserver v-slot="{ invalid }">
        <div v-if="!isDepAccountPartnerBank" class="edit-buttons">
          <div v-if="editMode">
            <!-- Close Edit Mode for existing deposits -->
            <CancelButton v-if="deposit.status !== 'new'" class="mr-1" @click.native="editMode = false" />
            <!-- Remove the draft deposit -->
            <CancelButton v-else class="mr-1" @click.native="removeDraftDeposit"></CancelButton>
            <SaveButton @click.native="updateDepositAndCloseEditMode" :disabled="invalid" class="ml-1" />
          </div>

          <div v-else>
            <DeleteButton class="mr-1" @click.native="showRemoveDepositModal = true" />
            <EditButton @click.native="editMode = true" class="ml-1" />
          </div>
        </div>

        <!-- Security Registration Forms -->
        <TenancyDepositRegisterForms
          v-if="editMode"
          :name="getTenancyDepositCategory"
          :deposit="deposit"
          @updatedLocalCopy="setLocalDeposit"
          @gotFiles="handleFilesUpload"
        />

        <div v-else class="deposit-card">
          <!-- / depAccount EasyBank release delay message -->
          <div class="p-2 text-center">
            <div class="mb-2">
              <h4 class="d-inline-block mb-0">{{ getTenancyDepositStatusName }}</h4>
              <button v-if="showReminder" class="btn btn-sm btn-danger d-inline-block ml-4">Remind tenant</button>
            </div>
          </div>

          <div class="border-radius-medium border-gray box-shadow-2 bg-light mt-4 p-5">
            <!-- ['deposit_guarantee', 'other_deposit_guarantee', 'other_deposit_account', 'bank_guarantee', 'nav_guarantee', 'other'] Default: [other] -->
            <h5>{{ getTenancyDepositCategory }} {{ getBankInfoOrNothing }}</h5>

            <!-- depAccount EasyBank release delay message -->
            <div v-if="shouldShowTerminationInfo">
              <hl-Alert v-if="!hasTenantAnsweredRelease" variant="info">
                {{ translationInitiatedRelease }}
              </hl-Alert>

              <hl-Alert v-else-if="hasTenantDeclinedRelease" variant="info">
                {{ translationTenantDeclinedRelease }}
              </hl-Alert>

              <hl-Alert v-else-if="hasTenantAnsweredWithRefund" variant="info">
                {{ translationAcceptedWithRefund }}
              </hl-Alert>

              <hl-Alert v-else-if="hasTenantAnsweredWithoutRefund" variant="info">
                {{ translationAcceptedWithoutRefund }}
              </hl-Alert>
            </div>
            <div class="row mt-5">
              <div v-if="!isDepOther" class="col d-flex">
                <BlockContainer class="p-5 flex-fill d-flex">
                  <div class="row align-items-center">
                    <div class="col-4 pr-4">
                      <div class="bank-logo">
                        <img v-if="bankInfo" :src="bankInfo.logo" class="img-fluid" />

                        <img
                          v-if="isDepAccountPartnerBank"
                          :src="require('@/assets/logos/lea-bank-logo.png')"
                          class="img-fluid"
                        />
                        <img
                          v-if="isDepGarSP"
                          :src="require('@/assets/logos/S_and_P.png')"
                          alt="S&P"
                          class="img-fluid"
                        />
                        <img v-if="isNAVGar" :src="require('@/assets/logos/NAV_logo.svg')" class="img-fluid" />
                      </div>
                      <h5 v-if="isDepGarOther && deposit.meta.supplier">{{ deposit.meta.supplier }}</h5>
                      <h5 v-if="isBankGar && deposit.bank">{{ deposit.bank }}</h5>
                    </div>

                    <div class="col-8 pl-4">
                      <div v-if="!isDepAccount && !isDepOther">
                        <p>{{ translationWarrantyNumber }}</p>

                        <h4 v-if="deposit.meta.policyNumber">{{ deposit.meta.policyNumber }}</h4>
                        <h4 v-else>{{ translationNotApplicable }}</h4>
                      </div>
                      <div v-if="isDepAccountPartnerBankSecondApiVersion" class="customer-identification">
                        <h6>{{ translationCustomerIdentification }}</h6>
                        <h6>{{ deposit.meta.kid }}</h6>
                      </div>
                      <div v-else-if="isDepAccount">
                        <p>{{ translationAccountNumber }}</p>
                        <h4 v-if="deposit.meta.bankAccount">{{ deposit.meta.bankAccount | formatBankAccount }}</h4>
                        <h4 v-else-if="deposit.meta.accountNumber">{{ deposit.meta.accountNumber | formatBankAccount }}</h4>
                        <h4 v-else>{{ translationNotApplicable }}</h4>
                      </div>
                    </div>
                  </div>
                </BlockContainer>
              </div>

              <div class="col d-flex">
                <BlockContainer class="p-5 flex-fill">
                  <div class="row">
                    <div class="col">
                      <p>Avtalt depositum</p>
                    </div>
                    <div class="col font-weight-bold">
                      <hl-Currency v-if="deposit.amount" :amount="parseFloat(deposit.amount)"></hl-Currency>
                      <span v-else>{{ translationNotApplicable }}</span>
                    </div>
                  </div>
                  <div v-if="isDepAccount || isDepOther" class="row">
                    <div class="col">
                      <p>Saldo</p>
                    </div>
                    <div class="col font-weight-bold">
                      <hl-Currency
                        v-if="deposit.meta.funds"
                        :amount="parseFloat(deposit.meta.funds > deposit.amount ? deposit.amount : deposit.meta.funds)"
                      ></hl-Currency>
                      <span v-else>{{ translationNotApplicable }}</span>
                    </div>
                  </div>
                  <div v-if="!isDepAccount" class="row">
                    <div class="col">
                      <p class="mb-0">Varighet</p>
                    </div>
                    <div class="col font-weight-bold">
                      <hl-Date v-if="deposit.meta.startDate" :date="deposit.meta.startDate"></hl-Date>
                      <span v-else>{{ translationNotApplicable }}&nbsp;</span>
                      -
                      <span v-if="deposit.meta.endDate">
                        <hl-Date :date="deposit.meta.endDate"></hl-Date>
                      </span>
                      <span v-else>&nbsp;{{ translationNotApplicable }}</span>
                    </div>
                  </div>
                  <div v-if="isDepOther && deposit.otherSecurityType" class="row mt-3 pt-1">
                    <div class="col">
                      <p>Type sikkerhet</p>
                    </div>
                    <div class="col font-weight-bold">
                      <p>{{ deposit.otherSecurityType }}</p>
                    </div>
                  </div>
                  <!-- NAV -->
                  <div v-if="isNAVGar">
                    <div v-if="deposit.NAVOffice" class="row">
                      <div class="col">
                        <p>NAV-kontor</p>
                      </div>
                      <div class="col font-weight-bold">
                        <p>{{ deposit.NAVOffice }}</p>
                      </div>
                    </div>
                    <div v-if="deposit.NAVContactPerson" class="row">
                      <div class="col">
                        <p>Kontaktperson</p>
                      </div>
                      <div class="col font-weight-bold">
                        <p>{{ deposit.NAVContactPerson }}</p>
                      </div>
                    </div>
                    <div v-if="deposit.NAVEmail" class="row">
                      <div class="col">
                        <p>E-post</p>
                      </div>
                      <div class="col font-weight-bold">
                        <p>{{ deposit.NAVEmail }}</p>
                      </div>
                    </div>
                    <div v-if="deposit.NAVPhone" class="row">
                      <div class="col">
                        <p>Telefon</p>
                      </div>
                      <div class="col font-weight-bold">
                        <p>{{ deposit.NAVPhone }}</p>
                      </div>
                    </div>
                  </div>
                  <!-- / NAV -->
                </BlockContainer>
              </div>
            </div>

            <!-- Showing files for all non depAccount categories -->
            <div v-if="!isDepAccountPartnerBank" class="mt-5">
              <div v-if="deposit.fileMeta">
                <h6>Kopi av dokumentasjon</h6>
                <div class="row mt-4">
                  <div class="col-12 col-md-3 d-flex flex-column align-items-center">
                    <a :href="deposit.fileMeta.path" class="mb-3">
                      <FileThumbnail
                        :contentType="deposit.fileMeta.contentType"
                        :fileName="deposit.fileMeta.fileName"
                        showDeleteButton
                      />
                    </a>
                    <hl-Button
                      size="sm"
                      outline
                      variant="danger"
                      :loading="deletingFile"
                      @click="showConfirmDeleteFileModal = true"
                    >
                      <i class="budicon-solid budicon-solid-trash"></i>
                      <span class="sr-only">Slett fil</span>
                    </hl-Button>
                  </div>
                </div>
              </div>
            </div>

            <div class="text-center mt-5">
              <div v-if="isDepGar">
                <a href="https://www.husleie.no/krav/depositumsgaranti/hiscox" target="_blank">
                  <button v-if="isDepGarSP" class="btn btn-sm btn-secondary mx-2">Meld krav</button>
                </a>
              </div>

              <router-link
                :to="{ name: 'tenancy_deposit_release_form_manual', params: { depId: deposit.id } }"
                v-if="isDepAccountPartnerBank"
                tag="button"
                class="btn btn-sm btn-secondary"
                :disabled="isDepAccountPartnerBankEnded"
              >
                {{ translationCloseDepAccount }}
              </router-link>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </BlockContainer>
    <hl-Modal
      v-if="showConfirmDeleteFileModal"
      class="text-center"
      iconClass="budicon-trash text-danger"
      :title="'Slette filen?'"
      dismisable
      @close="showConfirmDeleteFileModal = false"
    >
      <p class="mb-5">Denne handlingen kan ikke omgjøres.</p>
      <div>
        <button class="btn btn-danger mx-2" @click="deleteFile">Bekreft</button>
        <button class="btn btn-outline-secondary mx-2" @click="showConfirmDeleteFileModal = false">Avbryt</button>
      </div>
    </hl-Modal>
  </div>
</template>

<script>
import BlockContainer from '@/components/BlockContainer';
import TenancyDepositRegisterForms from '@/components/tenancy/deposit/TenancyDepositRegisterForms';
import CancelButton from '@/components/UI/buttons/CancelButton';
import DeleteButton from '@/components/UI/buttons/DeleteButton';
import EditButton from '@/components/UI/buttons/EditButton';
import SaveButton from '@/components/UI/buttons/SaveButton';
import FileThumbnail from '@/components/UI/FileThumbnail';
import { getDepositCategory, getDepositStatus } from '@/lib/depositMaps';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Alert from '@modules/husleie-framework/src/components/text/Alert.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import axios from 'axios';
import { mapActions } from 'vuex';

export default {
  components: {
    BlockContainer,
    CancelButton,
    DeleteButton,
    EditButton,
    SaveButton,
    FileThumbnail,
    TenancyDepositRegisterForms,
    'hl-Modal': Modal,
    'hl-Button': Button,
    'hl-Currency': Currency,
    'hl-Alert': Alert,
    'hl-Date': Date,
  },

  mixins: [i18nCommon],

  props: [
    'deposit',
    'landlordId',
    'tenancyId',
    'accountNumber',
    'amount',
    'balance',
    'bank',
    'bankLogo',
    'category',
    'cellPhone',
    'contactPerson',
    'department',
    'isEditMode',
    'endDate',
    'email',
    'id',
    'policyNumber',
    'startDate',
    'status',
    'supplier',
    'otherSecurityType',
    'NAVPhone',
    'NAVEmail',
    'NAVContactPerson',
    'NAVOffice',
  ],

  data() {
    return {
      bankInfo: null,
      editMode: false,
      localDeposit: {},
      showRemoveDepositModal: false,

      files: [],
      fileToken: null,
      showConfirmDeleteFileModal: false,
      deletingFile: false,
    };
  },

  computed: {
    getApiVersion() {
      return this.deposit && this.deposit.meta ? this.deposit.meta.api_version : '';
    },
    getBankInfoOrNothing() {
      if (this.bankInfo == null || this.bankInfo.name == null) return;
      return ` i ${this.bankInfo.name}`;
    },

    isDepAccountPartnerBank() {
      return this.deposit.category === 'easybank_deposit_account' && this.deposit.status !== 'ended';
    },

    isDepAccountPartnerBankEnded() {
      return this.deposit.status === 'ended';
    },

    // Probably should use a method that accepts a version number instead of this.
    // because of the urgency 1 label, this is according to me an accepted quickfix
    isDepAccountPartnerBankSecondApiVersion() {
      return this.deposit.category === 'easybank_deposit_account' && this.deposit.meta.api_version === '2';
    },

    isDepAccount() {
      return this.deposit.category === 'easybank_deposit_account' || this.deposit.category === 'other_deposit_account';
    },

    isDepAccountOther() {
      return this.deposit.category === 'other_deposit_account';
    },

    isDepGar() {
      return this.deposit.category === 'deposit_guarantee' || this.deposit.category === 'other_deposit_guarantee';
    },

    isDepGarSP() {
      return this.deposit.category === 'deposit_guarantee';
    },

    isDepGarOther() {
      return this.deposit.category === 'other_deposit_guarantee';
    },

    isBankGar() {
      return this.deposit.category === 'bank_guarantee';
    },
    isNAVGar() {
      return this.deposit.category === 'nav_guarantee';
    },
    isDepOther() {
      return this.deposit.category === 'other';
    },
    getTenancyDepositCategory() {
      return getDepositCategory(this.category);
    },
    getTenancyDepositStatus() {
      return getDepositStatus({
        status: this.deposit.status, // Status of the deposit solution
        category: this.deposit.category, // Category of deposit solution
        funds: this.deposit.meta.funds > this.deposit.amount ? this.deposit.amount : this.deposit.meta.funds, // Current funds of deposit, not available for all
        amount: this.deposit.amount, // Amount of deposit expected
        startDate: this.startDate, // Start date of deposit agreement
      });
    },
    getTenancyDepositStatusName() {
      return this.getTenancyDepositStatus.name;
    },
    getTenancyDepositStatusClass() {
      return this.getTenancyDepositStatus._class;
    },
    showReminder() {
      return this.getTenancyDepositStatus.status === 'createdReminder';
    },
    hasTenantAnsweredRelease() {
      return this.deposit.termination.answered;
    },
    shouldShowTerminationInfo() {
      return this.isDepAccountPartnerBank && this.deposit.termination;
    },
    hasTenantDeclinedRelease() {
      return this.hasTenantAnsweredRelease && !this.deposit.termination.accepted;
    },
    isRefundSumToLandlord() {
      return this.deposit.termination.splitAmount > 0;
    },
    hasTenantAnsweredWithRefund() {
      return this.hasTenantAnsweredRelease && this.isRefundSumToLandlord;
    },
    hasTenantAnsweredWithoutRefund() {
      return this.hasTenantAnsweredRelease && !this.isRefundSumToLandlord;
    },
    translationNotApplicable() {
      return this.translate('tenancy.deposit.not applicable');
    },
    translationWarrantyNumber() {
      return this.translate('tenancy.deposit.warranty number');
    },
    translationCustomerIdentification() {
      return this.translate('tenancy.deposit.customer identification');
    },
    translationAccountNumber() {
      return this.translate('tenancy.deposit.account number');
    },
    translationCloseDepAccount() {
      return this.translate('tenancy.deposit.close deposit account');
    },
    translationInitiatedRelease() {
      return this.translate('tenancy.deposit.release.initiated release');
    },
    translationAcceptedWithRefund() {
      return this.translate('tenancy.deposit.release.accepted with refund');
    },
    translationAcceptedWithoutRefund() {
      return this.translate('tenancy.deposit.release.accepted without refund');
    },
    translationTenantDeclinedRelease() {
      return this.translate('tenancy.deposit.release.tenant declined release');
    },
  },

  methods: {
    ...mapActions('tenancy', [
      'deleteTenancyDeposit',
      'updateTenancyDeposit',
      'deleteTenancyDeposit',
      'getBankInformationByAccountPrefix',
    ]),

    async deleteFile() {
      this.deletingFile = true;
      const data = JSON.parse(JSON.stringify(this.deposit));
      delete data.fileMeta;
      data.fileToken = '';
      try {
        await this.updateTenancyDeposit({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          depositId: this.deposit.id,
          data,
        });
        this.showConfirmDeleteFileModal = false;
      } catch (error) {
        console.error(error);
        this.$toast('Klarte ikke slette filen.', 'error');
      }
      this.deletingFile = false;
    },

    setLocalDeposit(deposit) {
      this.localDeposit = deposit;
    },

    removeDraftDeposit() {
      this.$emit('removeDraft', this.localDeposit.id);
    },

    async deleteDeposit() {
      try {
        await this.deleteTenancyDeposit({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          depositId: this.deposit.id,
        });
        this.showRemoveDepositModal = false;
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke slette depositum.', 'error');
      }
    },

    async updateDepositAndCloseEditMode() {
      if (this.files.length) {
        await this.submitFiles();
      }

      try {
        const localDepositCopy = JSON.parse(JSON.stringify(this.localDeposit));
        delete localDepositCopy.fileMeta;
        await this.updateTenancyDeposit({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          depositId: this.localDeposit.id,
          data: localDepositCopy,
        });
        this.editMode = false;
        //this.getBankInfo();
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke oppdatere depositum.', 'error');
      }
    },

    handleFilesUpload(files) {
      this.files = files;
    },

    async submitFiles() {
      try {
        const vue = this;
        const formData = new FormData();

        for (let i = 0; i < this.files.length; i++) {
          const file = this.files[i].file;
          formData.append('files[' + i + ']', file);
        }
        await axios
          .post('/upload/', formData, {
            withCredentials: true,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          .then(function (response) {
            console.log('response in axios', response);
            for (const file of response.data.uploaded_files) {
              console.log('setting file token to ', file.token);
              vue.localDeposit.fileToken = file.token;
              console.log('this.localDeposit', vue.localDeposit);
              //vue.postDocuments(file.token);
            }
          })
          .catch(function (error) {
            console.log('FAILURE!!', error);
          });
      } catch (error) {
        console.error(error);
      }
    },

    /*
    async postDocuments(token) {
      try {
        const res = await this.addTenancyDocument({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: {token}
        });
        this.files = [];
        //return res;
      } catch (error) {
        console.error(error);
      }
    },
    */

    async getBankInfo(prefix) {
      try {
        this.bankInfo = await this.getBankInformationByAccountPrefix(prefix);
      } catch (error) {
        console.log(error);
        this.$toast('Kunne ikke hente bank info', 'error');
      }
    },
  },

  mounted() {
    this.editMode = this.isEditMode;
    const accountNumber = this.deposit.meta.accountNumber;
    if (accountNumber) {
      const prefix = accountNumber.slice(0, 4);
      this.getBankInfo(prefix);
    }
  },
};
</script>

<style scoped lang="scss">
.deposit {
  position: relative;

  .edit-buttons {
    position: absolute;
    right: 3rem;
    top: 3rem;
  }

  .bank-logo > img {
    border: 0px;
    padding: 0.2rem;
    box-shadow: $box-shadow-1;
    width: 100%;
    max-width: 190px;
    min-height: 50px;
    object-fit: contain;
  }
}
.customer-identification {
  display: flex;
  column-gap: 34px;
  flex-wrap: wrap;
}
</style>
